/**
 * batch-61 welkomstmail (FO-448) — "Welkomstmail + inlog delen" voor
 * klantportaal-gebruikers en medewerkers.
 *
 * Gedeeld door de API (routes + service), de backoffice (accountkaart,
 * medewerkersprofiel, onboarding-cockpit) en de klant-app (gebruikersbeheer
 * door de klant-eigenaar). Eén Zod-bron voor de request-body's zodat het
 * formulier nooit iets kan sturen dat de API weigert.
 *
 * Regels (spec §4/§5): nooit een wachtwoord in een mail. Klant-accounts
 * krijgen een EENMALIGE wachtwoord-instellen-link met een eigen welkomst-
 * token (geldigheid instelbaar, default 7 dagen; hergebruikt de bestaande
 * reset-token-mechaniek en de `/wachtwoord-reset`-pagina). Medewerkers krijgen
 * de portal-URL + Microsoft Entra-inloguitleg.
 */
import { z } from 'zod'
import type { OutgoingEmailStatus } from './mail'

// ─── Trigger- en template-keys ──────────────────────────────────────────────

export const WELKOMSTMAIL_KLANT_TRIGGER_KEY = 'KLANTPORTAAL_WELKOMSTMAIL_INLOG' as const
export const WELKOMSTMAIL_MEDEWERKER_TRIGGER_KEY = 'MEDEWERKER_WELKOMSTMAIL_INLOG' as const

/** Locale-suffix-conventie voor mailtemplates (NL = basis-key, EN = basis-key + suffix). */
export const WELKOMSTMAIL_TEMPLATE_LOCALE_SUFFIX_EN = '-en'

/** NL-template van de klant-welkomstmail (gekoppeld aan de trigger). */
export const WELKOMSTMAIL_KLANT_TEMPLATE_KEY = 'klantportaal-welkomstmail-inlog'
/**
 * EN-variant: conventie `<templateKey>-en`. De service kiest per
 * `ClientPortalAccount.preferredLocale`; ontbreekt de EN-template, dan valt
 * hij terug op de NL-template (nooit een lege mail).
 */
export const WELKOMSTMAIL_KLANT_TEMPLATE_KEY_EN =
  WELKOMSTMAIL_KLANT_TEMPLATE_KEY + WELKOMSTMAIL_TEMPLATE_LOCALE_SUFFIX_EN
export const WELKOMSTMAIL_MEDEWERKER_TEMPLATE_KEY = 'medewerker-welkomstmail-inlog'

// ─── Token-geldigheid (instelbaar, spec A2) ─────────────────────────────────

export const WELKOMSTMAIL_TOKEN_GELDIGHEID_DEFAULT_DAGEN = 7
export const WELKOMSTMAIL_TOKEN_GELDIGHEID_MIN_DAGEN = 1
/**
 * Bovengrens bewust 14 dagen: de link met het eenmalige token staat (zoals
 * elke verzonden mail) leesbaar in de Uitbox voor MAIL.canView, dus een lange
 * geldigheid is ook een lang venster om een klantaccount over te nemen.
 */
export const WELKOMSTMAIL_TOKEN_GELDIGHEID_MAX_DAGEN = 14

/** SystemSetting-key voor de standaard-geldigheid (dagen) van de klant-inloglink. */
export const WELKOMSTMAIL_INSTELLING_KEY_TOKEN_DAGEN = 'welkomstmail.klantTokenGeldigheidDagen'

const geldigheidDagenSchema = z
  .number()
  .int()
  .min(WELKOMSTMAIL_TOKEN_GELDIGHEID_MIN_DAGEN, {
    message: `Minimaal ${WELKOMSTMAIL_TOKEN_GELDIGHEID_MIN_DAGEN} dag`,
  })
  .max(WELKOMSTMAIL_TOKEN_GELDIGHEID_MAX_DAGEN, {
    message: `Maximaal ${WELKOMSTMAIL_TOKEN_GELDIGHEID_MAX_DAGEN} dagen`,
  })

export const welkomstmailInstellingenSchema = z.object({
  klantTokenGeldigheidDagen: geldigheidDagenSchema,
})
export type WelkomstmailInstellingenDTO = z.infer<typeof welkomstmailInstellingenSchema>

// ─── Request-schema's ───────────────────────────────────────────────────────

/** Backoffice: klant-account "Welkomstmail + inlog delen". */
export const klantWelkomstmailVerzendSchema = z.object({
  /** Afwijkend adres (default: het login-adres van het account). */
  ontvanger: z
    .string()
    .trim()
    .email({ message: 'Vul een geldig e-mailadres in' })
    .toLowerCase()
    .max(254)
    .optional(),
  /** Geldigheid van de inloglink in dagen (default: de instelling). */
  geldigheidDagen: geldigheidDagenSchema.optional(),
  /** Sla `geldigheidDagen` op als nieuwe standaard (instelling). */
  bewaarAlsStandaard: z.boolean().optional(),
})
export type KlantWelkomstmailVerzendInput = z.infer<typeof klantWelkomstmailVerzendSchema>

export const WELKOMSTMAIL_ONTVANGER_SOORT = ['PRIVE', 'WERK'] as const
export type WelkomstmailOntvangerSoort = (typeof WELKOMSTMAIL_ONTVANGER_SOORT)[number]

/** Backoffice: medewerker "Welkomstmail + inlog delen". */
export const medewerkerWelkomstmailVerzendSchema = z.object({
  /** Naar het privé- of werkadres (default: privé als bekend, anders werk). */
  ontvanger: z.enum(WELKOMSTMAIL_ONTVANGER_SOORT).optional(),
})
export type MedewerkerWelkomstmailVerzendInput = z.infer<typeof medewerkerWelkomstmailVerzendSchema>

// ─── Status-DTO's ───────────────────────────────────────────────────────────

export type WelkomstmailVerstuurdDoorSoort = 'STREEP' | 'KLANT'

/** Eén verzending (de laatste wordt op de kaart getoond; alles staat in de audit). */
export interface WelkomstmailVerzendingDTO {
  id: string
  verstuurdOp: string
  verstuurdDoorNaam: string | null
  verstuurdDoorSoort: WelkomstmailVerstuurdDoorSoort | null
  ontvanger: string
  locale: 'nl' | 'en'
  opnieuw: boolean
  /** Klant: bevatte de mail een eenmalige wachtwoord-instellen-link? */
  metInlogLink: boolean
  tokenGeldigTot: string | null
  /** Klant: wanneer de link is ingewisseld (wachtwoord ingesteld). */
  tokenGebruiktOp: string | null
  /** Klant: link nog open (niet gebruikt, niet verlopen, niet vervangen)? */
  tokenOpen: boolean
  /** Medewerker: welkomsbrief-PDF (FO-382) meegestuurd? */
  metBijlage: boolean
  mailStatus: OutgoingEmailStatus | null
  /** Korte reden bij FAILED/BOUNCED/DROPPED (nooit de body). */
  mailFout: string | null
  /** Open-tracking (alleen wanneer de organisatie dat op de trigger aanzette). */
  eersteKeerGeopendOp: string | null
  laatstGeopendOp: string | null
}

export const WELKOMSTMAIL_BLOKKADE_CODES = [
  'GEEN_EMAIL',
  'ACCOUNT_INACTIEF',
  'GEEN_PORTAL_TOEGANG',
  'WERKACCOUNT_NIET_GEACTIVEERD',
  'GEBRUIKER_INACTIEF',
] as const
export type WelkomstmailBlokkadeCode = (typeof WELKOMSTMAIL_BLOKKADE_CODES)[number]

/** Waarom de knop uit staat + waar de gebruiker het kan oplossen. */
export interface WelkomstmailBlokkadeDTO {
  code: WelkomstmailBlokkadeCode
  uitleg: string
  /** Relatieve backoffice-link (bv. het medewerkersprofiel / gebruikersbeheer). */
  link: string | null
  linkLabel: string | null
}

export interface KlantWelkomstmailStatusDTO {
  kanVersturen: boolean
  blokkade: WelkomstmailBlokkadeDTO | null
  /** False zodra de gebruiker ooit inlogde: dan zonder link, met "wachtwoord vergeten"-verwijzing. */
  krijgtInlogLink: boolean
  standaardGeldigheidDagen: number
  ontvangerStandaard: string
  locale: 'nl' | 'en'
  aantalVerzendingen: number
  laatste: WelkomstmailVerzendingDTO | null
  /** Uit de klantportaal-audit (eerste LOGIN) en `lastLoginAt`. */
  eersteLoginOp: string | null
  laatsteLoginOp: string | null
}

export interface MedewerkerWelkomstmailStatusDTO {
  kanVersturen: boolean
  /** Mag de opvragende gebruiker versturen (MEDEWERKERS.canEdit)? De UI verbergt anders de knop. */
  actorMagVersturen: boolean
  blokkade: WelkomstmailBlokkadeDTO | null
  portalToegang: boolean
  werkaccountGeactiveerd: boolean
  /** Het Entra-werkaccount (login) zoals het in de mail komt; null zolang niet geactiveerd. */
  loginEmail: string | null
  ontvangerOpties: Array<{ soort: WelkomstmailOntvangerSoort; email: string }>
  ontvangerStandaard: WelkomstmailOntvangerSoort | null
  /** Is er een welkomsbrief (FO-382) in het dossier die als bijlage meegaat? */
  welkomsbriefBeschikbaar: boolean
  aantalVerzendingen: number
  laatste: WelkomstmailVerzendingDTO | null
  laatsteLoginOp: string | null
}

export interface WelkomstmailVerzendResultaatDTO {
  verzendingId: string
  ontvanger: string
  metInlogLink: boolean
  tokenGeldigTot: string | null
  metBijlage: boolean
  mailStatus: OutgoingEmailStatus
  /** Waarschuwing voor de UI (bv. bijlage kon niet gegenereerd worden); nooit blokkerend. */
  waarschuwing: string | null
}

